import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AppearanceService, ColorScheme } from '../appearance/appearance.service';
import { DisplayPreferencesService } from '../preferences/display-preferences.service';

export type UserProfilePreferences = {
  theme?: string | null;
  dateFormat?: string | null;
  timeFormat?: string | null;
  density?: string | null;
  compactSidebar?: boolean | null;
};

export type UserProfile = {
  locale?: string | null;
  userId?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  phone?: string | null;
  timeZone?: string | null;
  profileImageUrl?: string | null;
  profilePictureUrl?: string | null;
  avatarUrl?: string | null;
  photoUrl?: string | null;
  profileVersion?: number | null;
  securityVersion?: number | null;
  mfaEnabled?: boolean | null;
  authenticatorConfigured?: boolean | null;
  mfaSecretVerified?: boolean | null;
  recoveryCodesRemaining?: number | null;
  preferences?: UserProfilePreferences | null;
};

export type MfaChannel = 'AUTHENTICATOR_APP' | 'EMAIL' | 'SMS' | 'RECOVERY_CODE';

export type UserProfileUpdateRequest = {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  phone?: string | null;
  timeZone?: string | null;
  mfaEnabled?: boolean | null;
  securityVersion?: number | null;
  mfaCode?: string | null;
  mfaChannel?: MfaChannel | null;
  profileVersion?: number | null;
  preferences?: UserProfilePreferences | null;
};

export type ChangePasswordRequest = {
  oldPassword: string;
  password: string;
  confirmPassword: string;
  mfaCode?: string | null;
  mfaChannel?: MfaChannel | null;
};

export type SendMfaCodeRequest = {
  userId: string;
  email?: string | null;
  channel: MfaChannel;
  purpose: 'SENSITIVE_ACTIONS';
};

export type AuthenticatorSetupResponse = {
  manualEntryKey?: string | null;
  qrCodeImageBase64?: string | null;
};

export type RecoveryCodesResponse = {
  codes?: string[] | null;
  remaining?: number | null;
  generatedAt?: string | null;
};

export type SecurityAuditEvent = {
  id?: number | string | null;
  serviceName?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  entityLabel?: string | null;
  action?: string | null;
  actorId?: string | null;
  actorEmail?: string | null;
  createdAt?: string | null;
  detailsJson?: unknown;
};

export type AccountDeletionRequestStatus =
  | 'PENDING'
  | 'IN_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'COMPLETED'
  | 'CANCELED';

export type AccountDeletionRequest = {
  id: string;
  userId?: string | null;
  emailSnapshot?: string | null;
  displayNameSnapshot?: string | null;
  accountType?: string | null;
  requesterType?: string | null;
  sourceApp?: string | null;
  status: AccountDeletionRequestStatus;
  reason?: string | null;
  retentionNotes?: string | null;
  adminNotes?: string | null;
  decisionReason?: string | null;
  requestedAt?: string | null;
  reviewedAt?: string | null;
  reviewedByUserId?: string | null;
  reviewedByEmail?: string | null;
  completedAt?: string | null;
  completedByUserId?: string | null;
  completedByEmail?: string | null;
  canceledAt?: string | null;
  createdDate?: string | null;
  lastModifiedDate?: string | null;
  version?: number | null;
};

export type ResponseDto = {
  message?: string;
  statusCode?: number;
};

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private readonly http = inject(HttpClient);
  private readonly appearance = inject(AppearanceService);
  private readonly displayPreferences = inject(DisplayPreferencesService);
  private readonly base = environment.apiGatewayUrl;
  private readonly authBase = this.authApiBase();
  private readonly profileSubject = new BehaviorSubject<UserProfile | null>(null);
  private readonly profileLoadedSubject = new BehaviorSubject(false);
  private ensurePromise: Promise<UserProfile | null> | null = null;

  readonly profile$ = this.profileSubject.asObservable();
  readonly profileLoaded$ = this.profileLoadedSubject.asObservable();

  getProfile(): UserProfile | null {
    return this.profileSubject.value;
  }

  async ensureProfile(force = false): Promise<UserProfile | null> {
    const cached = this.getProfile();
    if (!force && cached) {
      return cached;
    }

    if (!this.ensurePromise) {
      this.ensurePromise = firstValueFrom(this.refreshProfile()).finally(() => {
        this.ensurePromise = null;
      });
    }

    return this.ensurePromise;
  }

  refreshProfile() {
    return this.http.get<UserProfile>(`${this.base}/api/v1/profile`, { withCredentials: true }).pipe(
      tap((profile) => {
        this.profileSubject.next(profile ?? null);
        this.applyProfilePreferences(profile ?? null);
        this.profileLoadedSubject.next(true);
      }),
      catchError((err) => {
        console.warn('[Backoffice ProfileService] profile lookup failed:', err);
        this.profileSubject.next(null);
        this.profileLoadedSubject.next(true);
        return of(null);
      }),
    );
  }

  updateProfile(request: UserProfileUpdateRequest) {
    return this.http.patch<UserProfile>(`${this.base}/api/v1/profile`, request, { withCredentials: true }).pipe(
      tap((profile) => {
        this.profileSubject.next(profile ?? null);
        this.applyProfilePreferences(profile ?? null);
      }),
    );
  }

  changePassword(request: ChangePasswordRequest) {
    return this.http.put<ResponseDto>(`${this.base}/api/v1/profile/change-password`, request, {
      withCredentials: true,
    });
  }

  sendSensitiveActionCode(request: SendMfaCodeRequest) {
    return this.http.post<ResponseDto>(`${this.base}/api/v1/user/send-token`, request, {
      withCredentials: true,
    });
  }

  setupAuthenticator(mfaCode?: string | null, mfaChannel?: MfaChannel | null) {
    return this.http.post<AuthenticatorSetupResponse>(
      `${this.base}/api/v1/user/mfa/authenticator/setup-authenticated`,
      {
        mfaCode: mfaCode || null,
        mfaChannel: mfaChannel || null,
      },
      { withCredentials: true },
    );
  }

  enableAuthenticator(code: string) {
    return this.http.post<void>(
      `${this.base}/api/v1/user/mfa/authenticator/enable-authenticated`,
      { code },
      { withCredentials: true },
    );
  }

  regenerateRecoveryCodes(mfaCode?: string | null, mfaChannel?: MfaChannel | null) {
    return this.http.post<RecoveryCodesResponse>(
      `${this.base}/api/v1/profile/recovery-codes`,
      {
        mfaCode: mfaCode || null,
        mfaChannel: mfaChannel || null,
      },
      { withCredentials: true },
    );
  }

  listSecurityActivity(page = 0) {
    return this.http.get<{ content: SecurityAuditEvent[]; number: number; last: boolean }>(`${this.authBase}/api/v1/audit/me/page?page=${page}&size=25&securityOnly=true`, {
      withCredentials: true,
    });
  }

  getActiveAccountDeletionRequest() {
    return this.http.get<AccountDeletionRequest | null>(`${this.authBase}/api/v1/account-deletion-requests/me/active`, {
      withCredentials: true,
    });
  }

  requestAccountDeletion(sourceApp = 'BACKOFFICE') {
    return this.http.post<AccountDeletionRequest>(
      `${this.authBase}/api/v1/account-deletion-requests/me`,
      {
        requesterType: sourceApp,
        sourceApp,
      },
      { withCredentials: true },
    );
  }

  cancelAccountDeletionRequest(requestId: string) {
    return this.http.post<AccountDeletionRequest>(
      `${this.authBase}/api/v1/account-deletion-requests/me/${encodeURIComponent(requestId)}/cancel`,
      {},
      { withCredentials: true },
    );
  }

  uploadProfileImage(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<UserProfile>(`${this.base}/api/v1/profile/image`, formData, {
        observe: 'events',
        reportProgress: true,
        withCredentials: true,
      })
      .pipe(
        tap((event) => {
          if (event.type === HttpEventType.Response) {
            this.profileSubject.next(event.body ?? null);
            this.applyProfilePreferences(event.body ?? null);
            this.profileLoadedSubject.next(true);
          }
        }),
      );
  }

  removeProfileImage() {
    return this.http
      .delete<UserProfile>(`${this.base}/api/v1/profile/image`, { withCredentials: true })
      .pipe(
        tap((profile) => {
          this.profileSubject.next(profile ?? null);
          this.applyProfilePreferences(profile ?? null);
        }),
      );
  }

  clearProfile(): void {
    this.displayPreferences.setProfilePreferences({});
    this.profileSubject.next(null);
    this.profileLoadedSubject.next(false);
  }

  private applyProfilePreferences(profile: UserProfile | null): void {
    this.displayPreferences.setProfilePreferences({
      dateFormat: profile?.preferences?.dateFormat, timeZone: profile?.timeZone,
      locale: profile?.locale, timeFormat: profile?.preferences?.timeFormat,
    });

    const preferences = profile?.preferences;
    if (!preferences) {
      return;
    }

    const theme = this.normalizeColorScheme(preferences.theme);
    if (theme && !this.appearance.hasStoredColorSchemePreference) {
      this.appearance.setColorScheme(theme);
    }

    this.displayPreferences.setDateFormatFromPreference(preferences.dateFormat);
  }

  private normalizeColorScheme(value: string | null | undefined): ColorScheme | null {
    const normalized = value?.trim().toLowerCase();
    return normalized === 'light' || normalized === 'dark' || normalized === 'system' ? normalized : null;
  }

  private authApiBase(): string {
    const trimmed = this.base.replace(/\/+$/, '');
    return trimmed.endsWith('/dev') ? `${trimmed}/auth` : trimmed;
  }
}
